import { createContext, useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import ProductDetails from "@/features/product-details/ProductDetails";
import { useFetchMenusQuery } from "../organizations/api/menusApi";
import { useAppDispatch } from "@/hooks";
import { fetchCategories } from "../categories/slice";
import useOrganizations, { OrganizationFetchConfig } from "../organizations/useOrganizations";
import { Menu, Order, Organization, OrgCategory } from "@/types";

export interface UserOrganizationContextType extends Partial<Organization> {
  menus?: Menu[];
  orders?: Order[];
  categories?: OrgCategory[];
}

const initialState = {
  content: undefined,
  menus: undefined,
  tables: undefined,
  orders: undefined,
  categories: undefined,
};

export const UserOrganizationContext = createContext<UserOrganizationContextType>(initialState);

interface Props extends OrganizationFetchConfig {
  showOrderDetails?: boolean;
  showContactForm?: boolean;
}

const UserOrganizationProvider = ({ showOrderDetails = true, ...rest }: Props) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const dispatch = useAppDispatch();
  const menuQuery = useFetchMenusQuery(organizationId);
  const { currentOrganization, getOrgDetails } = useOrganizations();

  useEffect(() => {
    if (organizationId) {
      getOrgDetails(rest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  if (menuQuery.isLoading) {
    return null;
  }

  return (
    <UserOrganizationContext.Provider
      value={{
        content: currentOrganization?.content ?? [],
        tables: currentOrganization?.tables ?? [],
        orders: currentOrganization?.orders ?? [],
        categories: currentOrganization?.categories ?? [],
        menus: menuQuery?.data,
      }}
    >
      <Outlet />

      {showOrderDetails ? <ProductDetails /> : null}
    </UserOrganizationContext.Provider>
  );
};

export default UserOrganizationProvider;
